import { motion } from "framer-motion";
import { MapPin, Mail, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import SEOHead from "@/components/SEOHead";

export default function CareersPage() {
  return (
    <main data-testid="careers-page">
      <SEOHead
        title="Careers | Work With Great Engineers — Venury"
        description="Join Venury and build production-grade products using AI-augmented workflows. We're always looking for talented engineers."
        url="https://venury.tech/careers"
      />
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <div className="hero-orb-teal" style={{ top: "-10%", right: "10%" }} />
        <div className="hero-orb-orange" style={{ bottom: "-20%", left: "-5%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1116]/40 to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-16 w-full">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em] mb-6"
          >
            Careers
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0, 0, 0.2, 1] }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight max-w-4xl leading-[1.05]"
          >
            Work With{" "}
            <span className="gradient-text-teal">Great Engineers</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl mt-8"
          >
            We're always looking for talented engineers who want to build
            production-grade products using AI-augmented workflows.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex items-center gap-2 mt-6 text-sm text-slate-400"
          >
            <MapPin size={14} className="text-teal-bright" />
            <span>Based in Bangalore, India</span>
          </motion.div>
        </div>
      </section>

      {/* What We Look For */}
      <section data-testid="what-we-look-for" className="py-20 md:py-28 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">What We Look For</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-16">
              Engineers Who Ship
            </h2>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.1}>
            {[
              { title: "Full-Stack Builders", desc: "You can go from database schema to polished UI. React, Node.js, Python, Go — what matters is you build complete things." },
              { title: "AI-Curious", desc: "You're excited about Claude, Cursor, and Copilot. You use AI tools daily and understand their strengths and limitations." },
              { title: "Product Thinkers", desc: "You don't just implement specs. You push back, suggest better UX, and care about the user experience beyond the ticket." },
              { title: "Domain Learners", desc: "FinTech, payments, trading — you're energized by complex domains and pick up business context quickly." },
              { title: "Quality Focused", desc: "You write tests, document your code, and build things that work in production. Not just in demos." },
              { title: "Startup Energy", desc: "You thrive in small, fast-moving teams. You own your work end-to-end and don't wait for someone to tell you what to do next." },
            ].map((item, i) => (
              <StaggerItem key={i}>
                <div data-testid={`look-for-${i}`} className="p-6 rounded-lg border border-white/5 hover:border-teal-primary/20 transition-colors duration-300 h-full">
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Open Application CTA */}
      <section data-testid="open-application-section" className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Get In Touch</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
              No Open Positions?
            </h2>
            <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-10 max-w-xl mx-auto">
              We're a small studio and don't always have formal openings — but we're always interested
              in hearing from strong engineers. Drop us a line with your background
              and what excites you about Venury.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="mailto:careers@venury.tech"
                data-testid="careers-email-cta"
                className="inline-flex items-center gap-2 bg-teal-primary hover:bg-teal-dark text-white font-bold text-sm px-8 py-3.5 rounded transition-colors duration-200"
              >
                <Mail size={16} />
                careers@venury.tech
              </a>
              <Link
                to="/contact"
                data-testid="careers-contact-cta"
                className="inline-flex items-center gap-2 border border-white/10 hover:border-teal-primary/40 text-white font-bold text-sm px-8 py-3.5 rounded transition-colors duration-200"
              >
                Contact Us <ArrowUpRight size={14} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </main>
  );
}
